import { useEffect, useMemo, useState } from "react";
import { Sun, Moon, Plus, Pencil, Trash2, X, AlertTriangle } from "lucide-react";
import {
  getNovasVendas, postNovaVenda, putNovaVenda, deleteNovaVenda,
} from "../../api";
import SideBarNavOperacao from "../../componets/SideBarNavOperacao";
import { KpiRow } from "../../components/dashboard/KpiCard";
import { StackedHorizontalBarChart } from "../../components/dashboard/StackedHorizontalBarChart";
import { TotalsTable } from "../../components/dashboard/TotalsTable";
import { useNavigate } from 'react-router-dom';


// ── Tema (mesma paleta usada nas demais telas do módulo Operacional) ──────────
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
});

// Cores de marca para os dois segmentos do backlog.
const COR_BACKLOG_2026 = () => "#A3003A";
const COR_BACKLOG_2027 = () => "#5C001F";

const fmtBRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtBRLCompact = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  return fmtBRL(n);
};
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

const ANO_ATUAL = new Date().getFullYear();

const FORM_VAZIO = { cliente: "", valor_contrato: "", mes_inserido: "", ano_venda: String(ANO_ATUAL), backlog_2026: "", backlog_2027: "" };

// mes_inserido é livre ("Julho/2026") — separa em mês/ano para permitir filtrar por cada parte.
const MESES_ORDEM = {
  janeiro: 1, fevereiro: 2, marco: 3, abril: 4, maio: 5, junho: 6,
  julho: 7, agosto: 8, setembro: 9, outubro: 10, novembro: 11, dezembro: 12,
};
const normalizarMes = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
function parseMesAno(mesInserido) {
  const partes = String(mesInserido || "").split("/").map((p) => p.trim());
  return { mes: partes[0] || "", ano: partes[1] || "" };
}

export default function NovasVendas() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);

  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [selectedId, setSelectedId] = useState(null);

  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAno, setFiltroAno] = useState("");

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [erroForm, setErroForm] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function carregarVendas() {
    setLoading(true);
    setErro("");
    try {
      const dados = await getNovasVendas();
      setVendas(dados || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }
  const navigate = useNavigate();

  useEffect(() => { carregarVendas(); }, []);

  // ── Opções de filtro ─────────────────────────────────────────────────────
  // Mês: derivado de mes_inserido (ex: "Julho/2026" → "Julho"), ordenado cronologicamente.
  const opcoesMes = useMemo(() => {
    const set = new Set(vendas.map((v) => parseMesAno(v.mes_inserido).mes).filter(Boolean));
    return Array.from(set).sort(
      (a, b) => (MESES_ORDEM[normalizarMes(a)] ?? 99) - (MESES_ORDEM[normalizarMes(b)] ?? 99)
    );
  }, [vendas]);

  // Cliente: nomes já cadastrados, para sugestão via dropdown (datalist) além da digitação livre.
  const opcoesCliente = useMemo(() => {
    const set = new Set(vendas.map((v) => v.cliente).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [vendas]);

  // ── Filtros (Cliente / Mês / Ano) ───────────────────────────────────────────
  // Ano não é um valor livre: reflete a qual backlog a venda pertence (2026 e/ou 2027).
  const vendasFiltradas = useMemo(() => {
    const q = filtroCliente.trim().toLowerCase();
    return vendas.filter((v) => {
      if (q && !String(v.cliente || "").toLowerCase().includes(q)) return false;
      const { mes } = parseMesAno(v.mes_inserido);
      if (filtroMes && mes !== filtroMes) return false;
      if (filtroAno === "2026" && !(Number(v.backlog_2026) > 0)) return false;
      if (filtroAno === "2027" && !(Number(v.backlog_2027) > 0)) return false;
      return true;
    });
  }, [vendas, filtroCliente, filtroMes, filtroAno]);

  const filtrosAtivos = Boolean(filtroCliente || filtroMes || filtroAno);
  function limparFiltros() {
    setFiltroCliente("");
    setFiltroMes("");
    setFiltroAno("");
  }

  // ── KPIs ─────────────────────────────────────────────────────────────────
  // Novas Vendas: ano_venda == ano atual · Backlog: ano_venda != ano atual · Geral: todos os registros.
  function calcularKpis(lista) {
    const valorTotal = lista.reduce((acc, v) => acc + Number(v.valor_contrato || 0), 0);
    const backlog2026 = lista.reduce((acc, v) => acc + Number(v.backlog_2026 || 0), 0);
    const backlog2027 = lista.reduce((acc, v) => acc + Number(v.backlog_2027 || 0), 0);
    const pct2026 = valorTotal ? (backlog2026 / valorTotal) * 100 : 0;
    const pct2027 = valorTotal ? (backlog2027 / valorTotal) * 100 : 0;
    return { valorTotal, backlog2026, backlog2027, pct2026, pct2027 };
  }

  const vendasNovasVendas = useMemo(
    () => vendasFiltradas.filter((v) => Number(v.ano_venda) === ANO_ATUAL),
    [vendasFiltradas]
  );
  const vendasBacklog = useMemo(
    () => vendasFiltradas.filter((v) => Number(v.ano_venda) !== ANO_ATUAL),
    [vendasFiltradas]
  );

  const kpisNovasVendas = useMemo(() => calcularKpis(vendasNovasVendas), [vendasNovasVendas]);
  const kpisBacklog = useMemo(() => calcularKpis(vendasBacklog), [vendasBacklog]);
  const kpisGeral = useMemo(() => calcularKpis(vendasFiltradas), [vendasFiltradas]);

  // ── Dados ordenados (maior → menor valor total), usados no gráfico e na tabela ──
  const vendasOrdenadas = useMemo(
    () => [...vendasFiltradas].sort((a, b) => Number(b.valor_contrato || 0) - Number(a.valor_contrato || 0)),
    [vendasFiltradas]
  );

  const dadosGrafico = useMemo(
    () => vendasOrdenadas.map((v) => ({
      cliente: v.cliente,
      backlog_2026: Number(v.backlog_2026 || 0),
      backlog_2027: Number(v.backlog_2027 || 0),
    })),
    [vendasOrdenadas]
  );

  const selecionada = vendas.find((v) => v.id === selectedId) || null;

  // ── Modal: abrir / salvar ────────────────────────────────────────────────
  function abrirModal(venda = null) {
    setErroForm("");
    if (venda) {
      setEditando(venda);
      setForm({
        cliente: venda.cliente,
        valor_contrato: String(venda.valor_contrato),
        mes_inserido: venda.mes_inserido,
        ano_venda: String(venda.ano_venda ?? ANO_ATUAL),
        backlog_2026: String(venda.backlog_2026),
        backlog_2027: String(venda.backlog_2027),
      });
    } else {
      setEditando(null);
      setForm(FORM_VAZIO);
    }
    setModalAberto(true);
  }

  async function salvarVenda() {
    const cliente = form.cliente.trim();
    const mesInserido = form.mes_inserido.trim();
    const anoVenda = parseInt(form.ano_venda, 10);
    const valorContrato = parseFloat(form.valor_contrato);
    const backlog2026 = parseFloat(form.backlog_2026 || 0);
    const backlog2027 = parseFloat(form.backlog_2027 || 0);

    if (!cliente) return setErroForm("Informe o cliente.");
    if (!mesInserido) return setErroForm("Informe o mês inserido.");
    if (!Number.isInteger(anoVenda) || anoVenda < 2000) return setErroForm("Informe um ano da venda válido.");
    if ([valorContrato, backlog2026, backlog2027].some(Number.isNaN)) {
      return setErroForm("Preencha os valores numéricos corretamente.");
    }
    const soma = Math.round((backlog2026 + backlog2027) * 100) / 100;
    if (Math.round(valorContrato * 100) / 100 !== soma) {
      return setErroForm(
        `O Valor Contrato deve ser igual à soma dos backlogs (2026 + 2027 = ${fmtBRL(soma)}).`
      );
    }

    setSalvando(true);
    setErroForm("");
    try {
      const payload = {
        cliente, mes_inserido: mesInserido,
        ano_venda: anoVenda,
        valor_contrato: valorContrato,
        backlog_2026: backlog2026,
        backlog_2027: backlog2027,
      };
      if (editando) {
        await putNovaVenda(editando.id, payload);
      } else {
        await postNovaVenda(payload);
      }
      setModalAberto(false);
      await carregarVendas();
    } catch (e) {
      setErroForm(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function excluirVenda(venda) {
    if (!venda) return;
    if (!window.confirm("Deseja realmente excluir esta venda?")) return;
    try {
      await deleteNovaVenda(venda.id);
      if (selectedId === venda.id) setSelectedId(null);
      await carregarVendas();
    } catch (e) {
      setErro(e.message);
    }
  }

  function montarKpiItems(kpisSecao) {
    return [
      { label: "Valor Total", value: loading ? "—" : fmtBRLCompact(kpisSecao.valorTotal), color: C.brand },
      { label: "Backlog 2026", value: loading ? "—" : fmtBRLCompact(kpisSecao.backlog2026), color: COR_BACKLOG_2026(isLight) },
      { label: "Backlog 2027", value: loading ? "—" : fmtBRLCompact(kpisSecao.backlog2027), color: COR_BACKLOG_2027(isLight) },
      { label: "% em 2026", value: loading ? "—" : fmtPct(kpisSecao.pct2026), color: COR_BACKLOG_2026(isLight) },
      { label: "% em 2027", value: loading ? "—" : fmtPct(kpisSecao.pct2027), color: COR_BACKLOG_2027(isLight) },
    ];
  }

  const kpiItemsNovasVendas = montarKpiItems(kpisNovasVendas);
  const kpiItemsBacklog = montarKpiItems(kpisBacklog);
  const kpiItemsGeral = montarKpiItems(kpisGeral);

  const colunasTabela = [
    { key: "cliente", label: "Cliente" },
    { key: "valor_contrato", label: "Valor Contrato", align: "right", render: (r) => fmtBRLCompact(r.valor_contrato), total: true, totalFormatter: fmtBRLCompact },
    { key: "mes_inserido", label: "Mês Inserido" },
    { key: "ano_venda", label: "Ano da Venda" },
    { key: "backlog_2026", label: "Total Backlog 2026", align: "right", render: (r) => fmtBRLCompact(r.backlog_2026), total: true, totalFormatter: fmtBRLCompact },
    { key: "backlog_2027", label: "Total Backlog 2027", align: "right", render: (r) => fmtBRLCompact(r.backlog_2027), total: true, totalFormatter: fmtBRLCompact },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavOperacao isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Novas Vendas</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate('/inicio')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
          </button>
        </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {/* KPIs */}
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
              Novas Vendas ({ANO_ATUAL})
            </h2>
            <KpiRow items={kpiItemsNovasVendas} C={C} />
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
              Backlog (Anos Anteriores)
            </h2>
            <KpiRow items={kpiItemsBacklog} C={C} />
          </section>

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
              Geral
            </h2>
            <KpiRow items={kpiItemsGeral} C={C} />
          </section>

          {/* Filtros */}
          <section style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
            padding: "14px 18px", display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap",
          }}>
            <Campo label="Cliente" C={C}>
              <input
                style={{ ...inputStyle(C), width: 220 }}
                type="text"
                list="novas-vendas-clientes"
                placeholder="Buscar ou selecionar cliente…"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
              />
              <datalist id="novas-vendas-clientes">
                {opcoesCliente.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Campo>
            <Campo label="Mês" C={C}>
              <select
                style={{ ...inputStyle(C), width: 160 }}
                value={filtroMes}
                onChange={(e) => setFiltroMes(e.target.value)}
              >
                <option value="">Todos</option>
                {opcoesMes.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Campo>
            <Campo label="Ano" C={C}>
              <select
                style={{ ...inputStyle(C), width: 120 }}
                value={filtroAno}
                onChange={(e) => setFiltroAno(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
            </Campo>
            {filtrosAtivos && (
              <button
                onClick={limparFiltros}
                style={{ background: "none", border: "none", cursor: "pointer", color: C.danger, fontSize: 12, fontWeight: 700, padding: "9px 0" }}
              >
                Limpar filtros
              </button>
            )}
          </section>

          {/* Gráfico */}
          <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
            <h2 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: C.text }}>
              Backlog por Cliente (2026 x 2027)
            </h2>
            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : dadosGrafico.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>
                {filtrosAtivos ? "Nenhuma venda encontrada com os filtros aplicados." : "Nenhuma venda cadastrada."}
              </div>
            ) : (
              <StackedHorizontalBarChart
                data={dadosGrafico}
                categoryKey="cliente"
                segments={[
                  { dataKey: "backlog_2026", name: "Backlog 2026", color: COR_BACKLOG_2026(isLight) },
                  { dataKey: "backlog_2027", name: "Backlog 2027", color: COR_BACKLOG_2027(isLight) },
                ]}
                valueFormatter={fmtBRLCompact}
                categoryWidth={160}
                C={C}
              />
            )}
          </section>

          {/* Tabela */}
          <section>
            <h2 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: C.text }}>
              Detalhamento de Vendas
            </h2>
            <TotalsTable
              columns={colunasTabela}
              rows={vendasOrdenadas}
              rowKey={(r) => r.id}
              onRowClick={(r) => setSelectedId(r.id === selectedId ? null : r.id)}
              selectedKey={selectedId}
              C={C}
            />
            <p style={{ margin: "8px 0 0", fontSize: 11, color: C.muted }}>
              Clique em uma linha para selecioná-la e habilitar as ações de edição/exclusão abaixo.
            </p>
          </section>

          {/* Gerenciamento */}
          <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
            <h2 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: C.text }}>
              Gerenciamento de Novas Vendas
            </h2>
            <p style={{ margin: "0 0 16px", fontSize: 12, color: C.muted }}>
              {selecionada
                ? <>Venda selecionada: <strong style={{ color: C.text }}>{selecionada.cliente}</strong></>
                : "Selecione uma venda na tabela acima para editar ou excluir."}
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
              <button onClick={() => abrirModal(null)} style={btnStyle(C.brand)}>
                <Plus size={15} /> Adicionar Nova Venda
              </button>
              <button
                onClick={() => abrirModal(selecionada)}
                disabled={!selecionada}
                style={{ ...btnStyle(C.brand, true), opacity: selecionada ? 1 : 0.45, cursor: selecionada ? "pointer" : "not-allowed" }}
              >
                <Pencil size={15} /> Editar Venda
              </button>
              <button
                onClick={() => excluirVenda(selecionada)}
                disabled={!selecionada}
                style={{ ...btnStyle(C.danger, true), opacity: selecionada ? 1 : 0.45, cursor: selecionada ? "pointer" : "not-allowed" }}
              >
                <Trash2 size={15} /> Excluir Venda
              </button>
            </div>
          </section>
        </main>
      </div>

      {/* Modal Adicionar / Editar */}
      {modalAberto && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 440, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>
                {editando ? "Editar Venda" : "Adicionar Nova Venda"}
              </p>
              <button onClick={() => setModalAberto(false)} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Campo label="Cliente" C={C}>
                <input style={inputStyle(C)} type="text" placeholder="Nome do cliente"
                  value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} />
              </Campo>

              <Campo label="Mês Inserido" C={C}>
                <input style={inputStyle(C)} type="text" placeholder="Ex: Julho/2026"
                  value={form.mes_inserido} onChange={(e) => setForm({ ...form, mes_inserido: e.target.value })} />
              </Campo>

              <Campo label="Ano da Venda" C={C}>
                <input style={inputStyle(C)} type="number" step="1" placeholder={String(ANO_ATUAL)}
                  value={form.ano_venda} onChange={(e) => setForm({ ...form, ano_venda: e.target.value })} />
              </Campo>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Campo label="Backlog 2026 (R$)" C={C}>
                  <input style={inputStyle(C)} type="number" min="0" step="0.01" placeholder="0.00"
                    value={form.backlog_2026} onChange={(e) => setForm({ ...form, backlog_2026: e.target.value })} />
                </Campo>
                <Campo label="Backlog 2027 (R$)" C={C}>
                  <input style={inputStyle(C)} type="number" min="0" step="0.01" placeholder="0.00"
                    value={form.backlog_2027} onChange={(e) => setForm({ ...form, backlog_2027: e.target.value })} />
                </Campo>
              </div>

              <Campo label="Valor Contrato (R$)" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" placeholder="0.00"
                  value={form.valor_contrato} onChange={(e) => setForm({ ...form, valor_contrato: e.target.value })} />
              </Campo>
              <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
                Deve ser igual à soma do Backlog 2026 + Backlog 2027.
              </p>

              {erroForm && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroForm}</p>}

              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                  onClick={() => setModalAberto(false)}>
                  Cancelar
                </button>
                <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }}
                  onClick={salvarVenda} disabled={salvando}>
                  {salvando ? "Salvando..." : editando ? "Atualizar" : "Cadastrar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers de UI locais ─────────────────────────────────────────────────────

function Campo({ label, children, C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none",
  };
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit",
  };
}
